import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play, Sparkles, Code2, Smartphone, Cloud, Star, Zap } from "lucide-react";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid={HERO.section}
      className="relative min-h-screen w-full overflow-hidden flex items-center justify-center noise"
    >
      {/* Background video */}
      <motion.div style={{ scale }} className="absolute inset-0 z-0">
        <video
          data-testid={HERO.video}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://images.pexels.com/photos/7562087/pexels-photo-7562087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        >
          <source
            src="https://cdn.pixabay.com/video/2023/10/20/185665-877611514_large.mp4"
            type="video/mp4"
          />
        </video>
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_50%_40%,transparent_0%,rgba(0,0,0,0.7)_80%)]" />
        {/* Aurora blobs */}
        <div className="aurora bg-[#0A84FF] h-[420px] w-[420px] -top-20 -left-20" />
        <div className="aurora bg-[#F59E0B] h-[300px] w-[300px] bottom-10 right-10 opacity-20" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 md:px-10 pt-28 md:pt-32 pb-20 md:pb-24 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 md:px-4 md:py-2 rounded-full glass text-[10px] md:text-[11px] tracking-[0.2em] uppercase font-bold text-[#a1a1a6]"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#0A84FF] animate-pulse" />
          New · Engineering Studio
        </motion.div>

        <div className="relative mt-6 md:mt-8">
        <h1 className="font-display font-black tracking-[-0.04em] leading-[0.85] text-[16vw] sm:text-[14vw] md:text-[14vw] lg:text-[10.5vw]">
          {"Engineered".split("").map((c, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 + i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block"
            >
              {c}
            </motion.span>
          ))}
          <br />
          <motion.span
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="shimmer inline-block italic font-display"
          >
            for the future.
          </motion.span>
        </h1>

        {/* Floating premium banners — pulled out of h1 to avoid inherited line-height */}
        <motion.div
          initial={{ opacity: 0, x: 40, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block absolute top-[6%] right-[1%] z-20"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="glass rounded-2xl px-5 py-3.5 flex items-center gap-3 shadow-[0_20px_60px_-15px_rgba(10,132,255,0.5)]"
          >
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#0A84FF] to-[#0a4ba8] grid place-items-center flex-none">
              <Sparkles size={16} strokeWidth={2} className="text-white" />
            </div>
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#0A84FF] font-bold">AI Native</div>
              <div className="text-sm font-medium text-white whitespace-nowrap">GPT · Claude · Gemini</div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -40, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block absolute top-[48%] right-[6%] z-20"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
            className="glass rounded-2xl px-5 py-3.5 flex items-center gap-3"
          >
            <div className="h-9 w-9 rounded-xl bg-white/[0.08] border border-white/10 grid place-items-center flex-none">
              <Star size={16} strokeWidth={2} className="text-[#F59E0B] fill-[#F59E0B]" />
            </div>
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">Rated 4.9</div>
              <div className="text-sm font-medium text-white whitespace-nowrap">120+ founders trust us</div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -40, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block absolute bottom-[-4%] right-[12%] z-20"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
            className="glass rounded-2xl px-5 py-3.5 flex items-center gap-3"
          >
            <div className="h-9 w-9 rounded-xl bg-white/[0.08] border border-white/10 grid place-items-center flex-none">
              <Zap size={16} strokeWidth={2} className="text-white" />
            </div>
            <div className="text-left leading-tight">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-bold">Ship velocity</div>
              <div className="text-sm font-medium text-white whitespace-nowrap">Weeks · not quarters</div>
            </div>
          </motion.div>
        </motion.div>
        </div>

        {/* Tech chip strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.45 }}
          className="mt-8 flex flex-wrap items-center gap-2 max-w-2xl"
        >
          {[
            { icon: Code2, label: "Web" },
            { icon: Smartphone, label: "Mobile" },
            { icon: Sparkles, label: "AI / ML" },
            { icon: Cloud, label: "Cloud" },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="glass inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium text-white/85"
            >
              <Icon size={13} strokeWidth={2} className="text-[#0A84FF]" />
              {label}
            </span>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-6 md:mt-8 max-w-2xl text-[#a1a1a6] text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed"
        >
          JDTECH is a senior engineering studio building AI products, web platforms, and mobile apps
          with the latest stacks. We design, we ship, we scale — quietly, obsessively.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-8 md:mt-10 flex flex-wrap items-center gap-3 md:gap-4"
        >
          <a
            href="mailto:contact@jdtechnologies.co"
            data-testid={HERO.ctaPrimary}
            className="btn-glow inline-flex items-center gap-2 rounded-full bg-white text-black px-5 sm:px-7 py-3 sm:py-4 text-sm md:text-base font-bold tracking-tight"
          >
            Start a project <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
          <button
            data-testid={HERO.ctaSecondary}
            onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 px-5 sm:px-7 py-3 sm:py-4 text-sm md:text-base font-medium transition-colors"
          >
            <Play size={14} strokeWidth={2.5} /> See what we do
          </button>
        </motion.div>

        {/* Mini stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 max-w-3xl"
        >
          {[
            ["120+", "Products shipped"],
            ["38", "Active clients"],
            ["12", "Countries served"],
            ["99.98%", "Uptime delivered"],
          ].map(([n, l]) => (
            <div key={l} className="glass rounded-xl md:rounded-2xl px-4 py-3 md:px-5 md:py-4">
              <div className="font-display text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">{n}</div>
              <div className="text-[10px] md:text-xs text-[#a1a1a6] uppercase tracking-[0.16em] md:tracking-[0.18em] mt-1">{l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#a1a1a6]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-8 w-[1px] bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
}
